// Müşteri profili › "Kanıt politikası" kartı (TODO-EVIDENCE-03): müşteri bazlı saklama süresi
// (referanssız kanıt + içe aktarma kaynağı) ve yeni dosya gizlilik varsayılanı override'ı.
// Boş alan = sistem geneli ayar (Sistem Ayarları → Kanıt Belgeleri); kart sistem değerini ipucu
// olarak gösterir. İzin: clients.edit_all (backend PUT assert'i ile aynı); izinsizde render edilmez.
// Dosya bazlı saklama (sistem / süresiz / N gün) hücre editörünün Kanıt sekmesinde (EvidencePanel).

import { useEffect, useState } from "react";
import {
  EVIDENCE_CONFIDENTIALITY,
  EVIDENCE_CONFIDENTIALITY_LABELS,
  type ClientEvidencePolicy,
  type ClientEvidencePolicyResponse,
} from "@mahir/shared";
import { api } from "../../api/client";
import { useAuthStore } from "../../stores/auth-store";

interface Props {
  clientId: string;
}

interface Draft {
  retentionDays: string;
  importRetentionDays: string;
  defaultConfidentiality: "" | (typeof EVIDENCE_CONFIDENTIALITY)[number];
}

export const CLIENT_EVIDENCE_POLICY_COPY = {
  title: "Kanıt politikası",
  hint: "Boş alan sistem geneli ayarı kullanır.",
  retentionDays: "Referanssız kanıt saklama (gün)",
  importRetentionDays: "İçe aktarma kaynağı saklama (gün)",
  defaultConfidentiality: "Yeni dosya gizliliği",
  systemValue: (v: string) => `sistem: ${v}`,
  days: (n: number) => (n === 0 ? "temizlik kapalı" : `${n.toString()} gün`),
  useSystem: "Sistem varsayılanı",
  edit: "Düzenle",
  save: "Kaydet",
  cancel: "Vazgeç",
  reset: "Sistem ayarına dön",
  override: "müşteriye özel",
  noOverride: "Sistem geneli ayarlar geçerli",
  loadError: "Kanıt politikası yüklenemedi",
  saveError: "Kaydedilemedi",
  saved: "Kaydedildi",
  invalidDays: "Gün 0–36500 arası tam sayı olmalı",
} as const;

const C = CLIENT_EVIDENCE_POLICY_COPY;

function toDraft(policy: ClientEvidencePolicy | null): Draft {
  return {
    retentionDays: policy?.retentionDays != null ? String(policy.retentionDays) : "",
    importRetentionDays:
      policy?.importRetentionDays != null ? String(policy.importRetentionDays) : "",
    defaultConfidentiality: policy?.defaultConfidentiality ?? "",
  };
}

/** Taslak → gövde; hepsi boşsa null (override yok). Geçersiz gün → hata mesajı. */
export function draftToPolicy(
  d: Draft,
): { policy: ClientEvidencePolicy | null } | { error: string } {
  const parseDays = (raw: string): number | null | "invalid" => {
    const t = raw.trim();
    if (t === "") return null;
    const n = Number(t);
    if (!Number.isInteger(n) || n < 0 || n > 36500) return "invalid";
    return n;
  };
  const r = parseDays(d.retentionDays);
  const i = parseDays(d.importRetentionDays);
  if (r === "invalid" || i === "invalid") return { error: C.invalidDays };
  const conf = d.defaultConfidentiality === "" ? null : d.defaultConfidentiality;
  if (r === null && i === null && conf === null) return { policy: null };
  return { policy: { retentionDays: r, importRetentionDays: i, defaultConfidentiality: conf } };
}

export function ClientEvidencePolicyCard({ clientId }: Props): JSX.Element | null {
  const me = useAuthStore((s) => s.user);
  const canEdit = me?.permissions["clients.edit_all"] === true;
  const [data, setData] = useState<ClientEvidencePolicyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>(toDraft(null));
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!canEdit) return;
    let aborted = false;
    api<ClientEvidencePolicyResponse>(`/clients/${clientId}/evidence-policy`)
      .then((res) => {
        if (aborted) return;
        setData(res);
        setDraft(toDraft(res.policy));
        setError(null);
      })
      .catch(() => {
        if (!aborted) setError(C.loadError);
      });
    return () => {
      aborted = true;
    };
  }, [clientId, canEdit]);

  if (!canEdit) return null;

  async function save(policy: ClientEvidencePolicy | null): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const res = await api<ClientEvidencePolicyResponse>(`/clients/${clientId}/evidence-policy`, {
        method: "PUT",
        body: { policy },
      });
      setData(res);
      setDraft(toDraft(res.policy));
      setEditing(false);
      setNotice(C.saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : C.saveError);
    } finally {
      setBusy(false);
    }
  }

  function submit(): void {
    const r = draftToPolicy(draft);
    if ("error" in r) {
      setError(r.error);
      return;
    }
    void save(r.policy);
  }

  const sys = data?.system;
  const policy = data?.policy ?? null;
  const labelStyle = { color: "var(--color-text-3)", fontSize: "0.72rem" } as const;
  const valueStyle = { color: "var(--color-text)", fontSize: "0.85rem" } as const;
  const inputStyle = {
    width: "100%",
    minHeight: 32,
    padding: "4px 8px",
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
    color: "var(--color-text)",
    fontSize: "0.85rem",
  } as const;

  return (
    <div
      data-testid="client-evidence-policy-card"
      className="rounded-xl p-5"
      style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
    >
      <div className="flex items-center justify-between mb-2">
        <h3
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "0.95rem",
            color: "var(--color-text)",
          }}
        >
          {C.title}
        </h3>
        {!editing && data ? (
          <button
            type="button"
            onClick={() => {
              setNotice(null);
              setEditing(true);
            }}
            className="text-xs"
            style={{ color: "var(--color-primary)", fontWeight: 600 }}
          >
            {C.edit}
          </button>
        ) : null}
      </div>

      {!data && !error ? (
        <p style={labelStyle}>Yükleniyor…</p>
      ) : !editing && sys ? (
        <dl className="flex flex-col gap-2">
          <div>
            <dt style={labelStyle}>{C.retentionDays}</dt>
            <dd style={valueStyle}>
              {policy?.retentionDays != null
                ? `${C.days(policy.retentionDays)} (${C.override})`
                : C.systemValue(C.days(sys.retentionDays))}
            </dd>
          </div>
          <div>
            <dt style={labelStyle}>{C.importRetentionDays}</dt>
            <dd style={valueStyle}>
              {policy?.importRetentionDays != null
                ? `${C.days(policy.importRetentionDays)} (${C.override})`
                : C.systemValue(C.days(sys.importRetentionDays))}
            </dd>
          </div>
          <div>
            <dt style={labelStyle}>{C.defaultConfidentiality}</dt>
            <dd style={valueStyle}>
              {policy?.defaultConfidentiality
                ? `${EVIDENCE_CONFIDENTIALITY_LABELS[policy.defaultConfidentiality]} (${C.override})`
                : C.systemValue(EVIDENCE_CONFIDENTIALITY_LABELS[sys.defaultConfidentiality])}
            </dd>
          </div>
          {!policy ? <p style={labelStyle}>{C.noOverride}</p> : null}
        </dl>
      ) : editing && sys ? (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <p style={labelStyle}>{C.hint}</p>
          <label className="flex flex-col gap-1">
            <span style={labelStyle}>
              {C.retentionDays} · {C.systemValue(C.days(sys.retentionDays))}
            </span>
            <input
              type="number"
              min={0}
              max={36500}
              step={1}
              inputMode="numeric"
              value={draft.retentionDays}
              placeholder={C.useSystem}
              onChange={(e) => setDraft({ ...draft, retentionDays: e.target.value })}
              style={inputStyle}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span style={labelStyle}>
              {C.importRetentionDays} · {C.systemValue(C.days(sys.importRetentionDays))}
            </span>
            <input
              type="number"
              min={0}
              max={36500}
              step={1}
              inputMode="numeric"
              value={draft.importRetentionDays}
              placeholder={C.useSystem}
              onChange={(e) => setDraft({ ...draft, importRetentionDays: e.target.value })}
              style={inputStyle}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span style={labelStyle}>
              {C.defaultConfidentiality} ·{" "}
              {C.systemValue(EVIDENCE_CONFIDENTIALITY_LABELS[sys.defaultConfidentiality])}
            </span>
            <select
              value={draft.defaultConfidentiality}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  defaultConfidentiality: e.target.value as Draft["defaultConfidentiality"],
                })
              }
              style={inputStyle}
            >
              <option value="">{C.useSystem}</option>
              {EVIDENCE_CONFIDENTIALITY.map((c) => (
                <option key={c} value={c}>
                  {EVIDENCE_CONFIDENTIALITY_LABELS[c]}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={busy}
              className="px-3 py-1.5 rounded text-sm"
              style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
            >
              {C.save}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setDraft(toDraft(policy));
                setEditing(false);
                setError(null);
              }}
              className="px-3 py-1.5 rounded text-sm"
              style={{ border: "1px solid var(--color-border)", color: "var(--color-text-2)" }}
            >
              {C.cancel}
            </button>
            {policy ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => void save(null)}
                className="text-xs ml-auto"
                style={{ color: "var(--color-text-3)" }}
              >
                {C.reset}
              </button>
            ) : null}
          </div>
        </form>
      ) : null}

      {error ? (
        <div role="alert" className="text-xs mt-2" style={{ color: "var(--color-error)" }}>
          {error}
        </div>
      ) : null}
      {notice ? (
        <div role="status" className="text-xs mt-2" style={{ color: "var(--color-success)" }}>
          ✓ {notice}
        </div>
      ) : null}
    </div>
  );
}
